import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { RetailColumnHeader } from '@/components/retail/RetailColumnHeader';
import {
  RETAIL_IDENTITY_CELL_CLASS,
  RETAIL_IDENTITY_FILL_CELL_CLASS,
  RETAIL_NUMERIC_CELL_CLASS,
  RETAIL_TABLE_CELL_CLASS,
  RETAIL_TABLE_CLASS,
  RETAIL_TABLE_FRAME_CLASS,
  RETAIL_TABLE_HEAD_CLASS,
  RETAIL_TABLE_SCROLL_CLASS,
} from '@/components/retail/retailTableLayout';
import { useRetailTableResize } from '@/components/retail/retailTableResize';

export type RetailTableColumn = {
  key: string;
  label: string;
  align?: 'left' | 'right';
  className?: string;
  identity?: boolean;
};

export function RetailDataTable({
  columns,
  rows,
  empty,
}: {
  columns: RetailTableColumn[];
  rows: Record<string, ReactNode>[];
  empty: string;
}) {
  const { tableRef } = useRetailTableResize(columns.map((col) => col.key).join('\0'));
  const fillLayout = columns.some((col) =>
    Boolean(col.className?.includes(RETAIL_IDENTITY_FILL_CELL_CLASS))
  );

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface px-4 py-10 text-center text-sm text-foreground-muted">
        {empty}
      </div>
    );
  }

  return (
    <div className={cn(RETAIL_TABLE_FRAME_CLASS, 'w-full max-w-full')}>
      <div className={RETAIL_TABLE_SCROLL_CLASS}>
        <table
          ref={tableRef}
          className={cn(RETAIL_TABLE_CLASS, 'text-sm', fillLayout && 'w-full table-fixed')}
        >
          <thead className="sticky top-0 z-10 border-b border-border bg-surface-header">
            <tr>
              {columns.map((col) => (
                <RetailColumnHeader
                  key={col.key}
                  label={col.label}
                  columnId={col.key}
                  align={col.align === 'right' ? 'right' : 'left'}
                  className={cn(
                    RETAIL_TABLE_HEAD_CLASS,
                    col.identity && RETAIL_IDENTITY_CELL_CLASS,
                    col.align === 'right' && RETAIL_NUMERIC_CELL_CLASS,
                    col.className
                  )}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="text-foreground hover:bg-surface-muted/80">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      RETAIL_TABLE_CELL_CLASS,
                      'h-full',
                      col.identity && RETAIL_IDENTITY_CELL_CLASS,
                      col.align === 'right' && RETAIL_NUMERIC_CELL_CLASS,
                      col.className
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-full items-center',
                        col.align === 'right' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {row[col.key]}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
